import React, { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Undo2 } from "lucide-react";

const deletedTemplates = [
  {
    id: 1,
    name: "Old Client Template",
    deletedOn: "2024-12-10",
    location: "San Francisco",
    tag: "Client A",
  },
  {
    id: 2,
    name: "Legacy Invoice",
    deletedOn: "2024-11-02",
    location: "New York",
    tag: "Finance",
  },
  {
    id: 3,
    name: "Startup Format v1",
    deletedOn: "2024-10-18",
    location: "Austin",
    tag: "Startup",
  },
  {
    id: 4,
    name: "Temporary Test",
    deletedOn: "2024-09-01",
    location: "Remote",
    tag: "QA",
  },
];

const tags = ["All", "Client A", "Finance", "Startup", "QA"];

const DeletedMessages = () => {
  const [activeTag, setActiveTag] = useState("All");

  const filteredTemplates =
    activeTag === "All"
      ? deletedTemplates
      : deletedTemplates.filter((item) => item.tag === activeTag);

  return (
    <motion.div
      className="px-fluid-3 py-fluid-4 max-w-7xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Breadcrumb / Title */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-fluid-3 gap-fluid-2">
        <div>
          <h2 className="text-fluid-2xl font-semibold text-gray-800">
            Deleted Invoice Templates
          </h2>
          <p className="text-fluid-base text-gray-600">
            View, restore, or permanently delete removed templates.
          </p>
        </div>
        <button className="text-blue-600 text-fluid-sm hover:underline">
          View All →
        </button>
      </div>

      {/* Search Input */}
      <div className="mb-fluid-3">
        <input
          type="text"
          placeholder="Search deleted templates..."
          className="w-full px-fluid-input-x py-fluid-input-y border border-gray-300 rounded-xl shadow-sm text-fluid-base focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2 mb-fluid-4">
        {tags.map((tag) => (
          <button
            key={tag}
            className={`px-3 py-1 rounded-full border text-fluid-sm ${
              activeTag === tag
                ? "bg-custom-bg-purple-solid text-white"
                : "border-gray-300 text-gray-600 hover:bg-gray-100"
            }`}
            onClick={() => setActiveTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Horizontal Scroll List */}
      <div className="flex space-x-fluid-2 overflow-x-auto pb-fluid-2 scrollbar-thin scrollbar-thumb-gray-300">
        {/* Add Template Button */}
        <motion.div
          whileTap={{ scale: 0.96 }}
          className="flex flex-col justify-center items-center w-60 h-36 border-2 border-dashed border-blue-400 text-blue-600 rounded-xl bg-white shadow-sm hover:shadow-md transition cursor-pointer flex-shrink-0"
        >
          <Plus size={20} />
          <span className="mt-2 text-fluid-sm font-medium">Add New Template</span>
        </motion.div>

        {/* Deleted Template Cards */}
        {filteredTemplates.map((template) => (
          <motion.div
            key={template.id}
            whileHover={{ scale: 1.02 }}
            className="w-60 h-44 bg-white rounded-xl shadow border border-gray-200 p-fluid-2 flex flex-col justify-between transition flex-shrink-0"
          >
            <div>
              <h3 className="text-fluid-lg font-semibold text-gray-800 truncate">
                {template.name}
              </h3>
              <p className="text-fluid-xs text-gray-500 mt-1">
                Deleted on {template.deletedOn}
              </p>
              <p className="text-fluid-xs text-gray-400">{template.location}</p>
              <p className="text-fluid-xs text-blue-600 mt-1 font-medium">
                #{template.tag}
              </p>
            </div>
            <div className="flex justify-between items-center text-fluid-sm mt-auto">
              <button className="text-blue-600 hover:underline flex items-center gap-1">
                <Undo2 size={16} /> Restore
              </button>
              <button className="text-red-500 hover:underline flex items-center gap-1">
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default DeletedMessages;
